import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Compass, GraduationCap, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SOURCES = [
  "Google Search",
  "Instagram / Facebook",
  "YouTube",
  "Friend / Family referral",
  "Walked past the branch",
  "Newspaper / Local ad",
  "Other",
];

const purposeOptions = [
  {
    key: "Coaching",
    Icon: GraduationCap,
    title: "Coaching",
    desc: "IELTS · PTE · Spoken English · Personality & Interview training",
  },
  {
    key: "Immigration",
    Icon: Compass,
    title: "Immigration",
    desc: "Study Abroad, university shortlisting, visa & PR pathways",
  },
];

const stepVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

export default function SurveyPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({ source: "", purpose: "", tried_other: "" });
  const [submitting, setSubmitting] = useState(false);

  const total = 3;
  const progress = (step / total) * 100;

  const canNext =
    (step === 1 && answers.source) ||
    (step === 2 && answers.purpose) ||
    (step === 3 && answers.tried_other);

  const handleNext = async () => {
    if (step < total) {
      setStep((s) => s + 1);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/survey", answers);
      await refreshUser();
      toast.success("Survey saved. Personalising your path…");
      navigate(data.purpose === "Immigration" ? "/immigration" : "/coaching", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grain relative overflow-hidden">
      <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-brand-red/10 blur-3xl animate-blob" />
      <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-brand-gold/15 blur-3xl animate-blob" style={{ animationDelay: "6s" }} />

      <div className="relative max-w-3xl mx-auto px-6 py-10 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-red grid place-items-center font-black text-white brand-shadow">L</div>
            <div>
              <div className="font-heading font-extrabold text-brand-navy">Language Nest India</div>
              <div className="text-[10px] uppercase tracking-widest text-brand-red font-bold">Step {step} of {total}</div>
            </div>
          </div>
          <div className="text-sm text-brand-navy/60 hidden sm:block">Takes 30 seconds</div>
        </div>

        <Progress value={progress} className="h-2 bg-brand-navy/10" data-testid="survey-progress" />

        <div className="mt-12 md:mt-16 min-h-[420px]">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.4 }}>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Question 1
                </div>
                <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl text-brand-navy leading-tight">
                  Where did you hear about us?
                </h2>
                <p className="mt-3 text-brand-navy/60">Helps us thank the right people (and improve).</p>

                <div className="mt-8 grid sm:grid-cols-2 gap-3">
                  {SOURCES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setAnswers((a) => ({ ...a, source: s }))}
                      data-testid={`survey-source-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`text-left px-5 py-4 rounded-2xl border-2 transition-all font-semibold ${
                        answers.source === s
                          ? "border-brand-red bg-brand-red/5 text-brand-red shadow-md"
                          : "border-brand-navy/10 hover:border-brand-navy/30 text-brand-navy"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{s}</span>
                        {answers.source === s && <Check className="w-4 h-4" />}
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.4 }}>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Question 2
                </div>
                <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl text-brand-navy leading-tight">
                  Why do you want to enroll?
                </h2>
                <p className="mt-3 text-brand-navy/60">We&#39;ll route you to the right path immediately.</p>

                <div className="mt-8 grid md:grid-cols-2 gap-5">
                  {purposeOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setAnswers((a) => ({ ...a, purpose: opt.key }))}
                      data-testid={`survey-purpose-${opt.key.toLowerCase()}`}
                      className={`text-left p-7 rounded-3xl border-2 transition-all ${
                        answers.purpose === opt.key
                          ? "border-brand-red bg-brand-red/5 shadow-lg -translate-y-1"
                          : "border-brand-navy/10 hover:border-brand-navy/30"
                      }`}
                    >
                      <div className={`w-14 h-14 rounded-2xl grid place-items-center mb-4 ${
                        answers.purpose === opt.key ? "bg-brand-red text-white" : "bg-brand-muted-bg text-brand-navy"
                      }`}>
                        <opt.Icon className="w-7 h-7" />
                      </div>
                      <div className="font-heading font-black text-2xl text-brand-navy">{opt.title}</div>
                      <div className="text-sm text-brand-navy/60 mt-1">{opt.desc}</div>
                      {answers.purpose === opt.key && (
                        <div className="mt-4 inline-flex items-center gap-1 text-brand-red font-semibold text-sm">
                          <Check className="w-4 h-4" /> Selected
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.4 }}>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Question 3
                </div>
                <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl text-brand-navy leading-tight">
                  Tried other coaching institutes before?
                </h2>
                <p className="mt-3 text-brand-navy/60">No judgement — just helps us plan your onboarding.</p>

                <div className="mt-8 grid grid-cols-2 gap-5 max-w-lg">
                  {["Yes", "No"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setAnswers((a) => ({ ...a, tried_other: v }))}
                      data-testid={`survey-triedother-${v.toLowerCase()}`}
                      className={`p-8 rounded-3xl border-2 transition-all text-center font-heading font-black text-3xl ${
                        answers.tried_other === v
                          ? "border-brand-red bg-brand-red text-white shadow-lg -translate-y-1"
                          : "border-brand-navy/10 hover:border-brand-navy/30 text-brand-navy"
                      }`}
                    >
                      <Building2 className={`w-8 h-8 mx-auto mb-3 ${answers.tried_other === v ? "text-white" : "text-brand-navy/50"}`} />
                      {v}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => step > 1 && setStep((s) => s - 1)}
            disabled={step === 1}
            data-testid="survey-back-btn"
            className="rounded-full text-brand-navy font-semibold disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={!canNext || submitting}
            data-testid="survey-next-btn"
            className="rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold px-8 h-12 brand-shadow btn-scale disabled:opacity-40"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : step === total ? (
              <>Finish <Check className="w-4 h-4 ml-2" /></>
            ) : (
              <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
